/**
 * Application id aliases.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
 * The app registry holds two entries for what is, to a customer, one product: a desktop
 * or back-office application and its web counterpart. Reports were created against one
 * of them, while user licences are assigned against the other. So a user licensed for
 * the web application could see none of the reports, because every report carried the
 * other id.
 *
 *   6331884c3444daff7180b691   Web Neurolage-WholeSale   <- licences point here
 *   5ed4cc1090c007f7203914d2   Neurolage                 <- reports live here
 *
 * Two ways to fix that: move every existing report onto the licensed id and change the
 * ERP site to accept either, or translate the licensed id to the report id here. The
 * scheduler team chose the second - it leaves live report data alone and needs no change
 * in the ERP site, which already reads the report id.
 *
 * ── WHAT THIS IS NOT ─────────────────────────────────────────────────────────
 * A bridge, not a design. The translation only exists inside this frontend, so anything
 * else that reads or writes reports - the ERP site, a future application - still sees
 * the two ids as unrelated. That is acceptable while it keeps one customer working; it
 * is not an answer to the registry holding duplicate entries.
 *
 * When the underlying data is reconciled, delete the entry and both functions below
 * become identity - nothing else has to change.
 *
 * Only the Neurolage pair is mapped. The other applications may well have the same split
 * and simply have not been noticed yet; each needs confirming before being added, since
 * guessing a pair would file reports under an application they do not belong to.
 */

/* licensed application id -> the application id reports are stored under */
export const APP_ID_ALIASES = {
  // Web Neurolage-WholeSale -> Neurolage
  "6331884c3444daff7180b691": "5ed4cc1090c007f7203914d2",
};

/**
 * The application id to store against a report, or to filter reports by, given the
 * application the user selected.
 *
 * Anything without an alias passes straight through, so this is safe to call everywhere
 * an application id is read or written for a report.
 */
export const reportAppIdFor = (applicationId) =>
  (applicationId && APP_ID_ALIASES[applicationId]) || applicationId || "";

/* The table read the other way round, built once. */
const REVERSE_ALIASES = Object.entries(APP_ID_ALIASES).reduce((acc, [licensed, stored]) => {
  acc[stored] = licensed;
  return acc;
}, {});

/**
 * The application id a user's dropdown would hold, given the id stored on a record.
 *
 * The inverse of reportAppIdFor, and needed when filling a form from saved data: a
 * schedule stores the report's application id, but the Application select offers the ids
 * the user is licensed for. Those differ for an aliased application, so the stored id
 * matched no option and the field came back empty on every edit.
 *
 * Only meaningful while the mapping is one-to-one. If two licensed applications are ever
 * aliased to the same stored id, this has to take the available options into account -
 * there would be no single right answer without them.
 */
export const licensedAppIdFor = (storedAppId) =>
  (storedAppId && REVERSE_ALIASES[storedAppId]) || storedAppId || "";

/**
 * The reverse: every application id that should be treated as the given one.
 *
 * Used where a report's stored id has to be matched back to a selected application -
 * both the alias target and the licensed id itself count, so a report created before the
 * alias existed is still found.
 */
export const reportAppIdsFor = (applicationId) => {
  if (!applicationId) return [];

  const aliased = APP_ID_ALIASES[applicationId];
  return aliased ? [applicationId, aliased] : [applicationId];
};

export default reportAppIdFor;
